import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  SnackbarProps,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party

import { ColumnDef } from "@tanstack/react-table";

// project-import

import IconButton from "components/@extended/IconButton";
import { FormattedMessage } from "react-intl";

// assets
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  ProfileOutlined,
  QuestionOutlined,
} from "@ant-design/icons";

import { openSnackbar } from "api/snackbar";

import Loader from "components/Loader";

import FiltersModal from "./FiltersModal";
import FormCompanyAccountAdd from "./FormCompanyAccountAdd";
import { CompanyProfileModel } from "../../../../../model/companyProfileModel";
import { USERAXIOS } from "api/userAxios";
import { CompaniesAccountFiltersType } from "../../../../../types/Filter";
import ReusableTable from "../../../../../components/ReusableComponent/ReusableTable";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";

const CompaniesAccounts = () => {
  const theme = useTheme();
  const location = useLocation();
  const [openFilters, setOpenFilters] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [customerDeleteId, setCustomerDeleteId] = useState<any>("");
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [selectedCompanyAccount, setSelectedCompanyAccount] =
    useState<CompanyProfileModel | null>(null);
  const [result, setResult] = useState<CompanyProfileModel[]>([]);
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const [isClearFilters, setClearFilters] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const handleAddModalClose = () => {
    setOpenAddModal(false);
    setSelectedCompanyAccount(null);
  };

  const handleAddNewCompany = () => {
    setSelectedCompanyAccount(null);
    setOpenAddModal(true);
  };

  const handleSearch = () => {
    setLoadingSearch(true);
    setCurrenttPage(1);
    setSearchText(tempSearchText);
    setLoadingSearch(false);
  };

  const columns = useMemo<ColumnDef<CompanyProfileModel>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">{row?.original.id}</Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="User Name" />,
        accessorKey: "user_name",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as string}
              </Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="Company Name" />,
        accessorKey: "name_en",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as string}
              </Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="Contact Person" />,
        accessorKey: "name_en",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as string}
              </Typography>
              <Typography color="text.secondary"></Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Location" />,
        accessorKey: "location",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {`${row.original.country}, ${row.original.city}`}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Registration Date" />,
        accessorKey: "registration_date",
        cell: ({ row, getValue }) => (
          <Stack
            direction="row"
            justifyContent="center"
            spacing={1.5}
            alignItems="center"
          >
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as number}
              </Typography>
              <Typography color="text.secondary"></Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="packages" />,
        accessorKey: "packages",
        cell: ({ row, getValue }) => (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="center"
            gap="1rem"
          >
            <QuestionOutlined
              style={{
                fontSize: "1.1rem",
                cursor: "pointer",
              }}
            />
            <ProfileOutlined
              style={{
                fontSize: "1.1rem",
                cursor: "pointer",
              }}
            />
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as string}
              </Typography>
              <Typography color="text.secondary"></Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as string}
              </Typography>
              <Typography color="text.secondary"></Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row, getValue }) => {
          const { user_id, id } = row.original;

          const handleViewProfile = () => {
            window.location.href = `/companies-list/view-company-details/${id}`;
          };
          const handleEditProfile = () => {
            setSelectedCompanyAccount(row.original);
            setOpenAddModal(true);
          };

          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title="View Details">
                <IconButton
                  color="primary"
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={handleViewProfile}
                >
                  <EyeOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Profile">
                <IconButton
                  color="primary"
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={handleEditProfile}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setCustomerDeleteId(row.original.id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [theme]
  );

  const fetchData = () => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const account_status = urlParams.get("account_status") || "";
    const account_language = urlParams.get("account_language") || "";
    const created_at_start = urlParams.get("created_at_start") || "";
    const created_at_end = urlParams.get("created_at_end") || "";

    const url =
      `/users?account_type=COMPANY?page=${currentPage}` +
      (searchText ? `&title=${searchText}` : "") +
      (account_status ? `&account_status=${account_status}` : "") +
      (account_language ? `&account_language=${account_language}` : "") +
      (created_at_start ? `&created_at_start=${created_at_start}` : "") +
      (created_at_end ? `&created_at_end=${created_at_end}` : "");

    USERAXIOS.get(url)
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching companies:", error);
        openSnackbar({
          open: true,
          message: "Error fetching companies",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText]);

  useEffect(() => {
    fetchData();
  }, [location.search]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page); // Update the currentPage state when the page changes
  };

  const handleFiltersClose = () => {
    setOpenFilters(false);
  };

  const clearFilters = () => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("account_status");
    urlParams.delete("account_language");
    urlParams.delete("created_at_start");
    urlParams.delete("created_at_end");
    window.history.pushState({}, "", `${window.location.pathname}`);
    setFiltersResetKey((prev) => prev + 1);
    setClearFilters(true);
    USERAXIOS.get(
      `/users?account_type=COMPANY?page=${currentPage}` +
        (searchText ? `&title=${searchText}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching companies:", error);
        openSnackbar({
          open: true,
          message: "Error fetching companies",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const hasActiveFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(
      urlParams.get("account_status") ||
      urlParams.get("account_language") ||
      urlParams.get("created_at_start") ||
      urlParams.get("created_at_end")
    );
  };

  const handleFiltersApply = (filters: CompaniesAccountFiltersType) => {
    setLoading(true);
    const {
      account_status,
      account_language,
      created_at_start,
      created_at_end,
    } = filters;
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("account_status");
    urlParams.delete("account_language");
    urlParams.delete("created_at_start");
    urlParams.delete("created_at_end");

    if (created_at_start) urlParams.set("created_at_start", created_at_start);
    if (account_status) urlParams.set("account_status", account_status);
    if (account_language) urlParams.set("account_language", account_language);
    if (created_at_end) urlParams.set("created_at_end", created_at_end);
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);
    USERAXIOS.get(
      `/users?account_type=COMPANY?page=${currentPage}` +
        (account_status ? `&account_status=${account_status}` : "") +
        (account_language ? `&account_language=${account_language}` : "") +
        (created_at_start ? `&created_at_start=${created_at_start}` : "") +
        (created_at_end ? `&created_at_end=${created_at_end}` : "") +
        (searchText ? `&title=${searchText}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching companies:", error);
        openSnackbar({
          open: true,
          message: "Error fetching companies",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await USERAXIOS.delete(`/users/${customerDeleteId}`);
      setResult((prevResults) =>
        prevResults.filter((item) => item.id !== customerDeleteId)
      );
      handleClose();
      openSnackbar({
        open: true,
        message: "Company account deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);
    } catch (error) {
      console.error("Error deleting company account:", error);
      openSnackbar({
        open: true,
        message: "Failed to delete company account",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            {hasActiveFilters() && (
              <Button
                variant="outlined"
                color="primary"
                onClick={clearFilters}
                sx={{ mb: 2, padding: ".3rem 2rem " }}
              >
                <FormattedMessage id="Clear Filters" />
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<FilterOutlined />}
              onClick={() => setOpenFilters(true)}
              sx={{
                mb: 2,
                padding: ".3rem 2rem",
                gap: ".5rem",
              }}
            >
              <FormattedMessage id="Filters" />
            </Button>
          </Box>
          <ReusableTable
            data={result}
            columns={columns}
            countPage={countPage}
            handlePageChange={handlePageChange}
            handleSearchChange={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrenttPage={setCurrenttPage}
            loadingSearsh={loadingSearch}
            searchedtext={tempSearchText}
            setSearchedtext={setTempSearchText}
            modalToggler={handleAddNewCompany}
            renderDetailPanel={({ row }: any) => <></>}
          />
        </>
      )}

      <FiltersModal
        open={openFilters}
        handleClose={handleFiltersClose}
        onApplyFilters={handleFiltersApply}
        onClearFilters={clearFilters}
        isClearFilters={isClearFilters}
      />

      <ReusableDeleteDialog
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <FormCompanyAccountAdd
        open={openAddModal}
        companyAccount={selectedCompanyAccount}
        loading={loading}
        setLoading={setLoading}
        handleClose={handleAddModalClose}
        setResult={setResult}
        fetchData={fetchData}
      />
    </>
  );
};

export default CompaniesAccounts;
